import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from './supabase';
import { Plus, Trash2, Copy, X, Save, ArrowLeft, FlaskConical, Lock } from 'lucide-react';
import { cn } from './ui/Table';
import { DataGrid, GridCol } from './ui/DataGrid';
import { Button } from './ui/arc/button';
import { Badge } from './ui/arc/badge';
import { Input } from './ui/arc/input';
import { Textarea } from './ui/arc/textarea';
import { Checkbox } from './ui/arc/checkbox';
import { EmptyState } from './ui/arc/empty-state';
import { PageHeader, StatCard, Panel } from './costs/plata-kit';

// Importaciones v3 — caso completo de costeo de importación argentina (migr 086 + 089):
//   FOB + flete internacional + seguro = CIF
//   sobre CIF: derechos de importación (POR PRODUCTO) + tasa estadística
//   sobre (CIF + derechos + tasa): IVA, IVA adicional, percepción Ganancias, percepción IIBB
//     → todos DEDUCIBLES / a cuenta: son plata que se pone pero no costo real
//   + gastos locales (despachante, depósito fiscal, transporte interno, transferencias,
//     imprevistos…) cargados CON IVA incluido — el sistema separa el crédito fiscal
//   Resultado: desembolso total, total deducible, COSTO REAL, DOS coeficientes s/FOB
//   (bruto = desembolso/FOB, y neto = costo real/FOB) y costo unitario real por producto.
//   v3 (21/09): el flete se separa en parte EXENTA (va al CIF) y parte GRAVADA con IVA
//   (servicios locales del forwarder: fuera del CIF, su IVA es crédito fiscal).
//   Cada producto tiene DOS valores: fob_unit_usd = lo que REALMENTE se paga (la factura)
//   y fob_declared_unit_usd = lo declarado ante aduana si es MENOR (vacío = se declara
//   todo). Los impuestos salen del declarado; el costo real, de lo pagado. Migr 090
//   invirtió esta semántica: antes la 1ra columna era el declarado y bajarla no reducía
//   impuestos como uno espera.

interface Gasto { name: string; amount_ars: number; has_iva: boolean }

interface ImportRow {
    id?: number;
    fecha: string;
    descripcion: string;
    exchange_rate: number;
    freight_usd: number;          // flete internacional EXENTO → entra al CIF
    freight_taxable_usd: number;  // servicios locales del forwarder, IVA incluido → crédito fiscal
    insurance_usd: number;
    tasa_estadistica_pct: number;
    iva_pct: number;
    iva_adic_pct: number;
    ganancias_pct: number;
    iibb_pct: number;
    gastos: Gasto[];
    is_hypothetical: boolean;
    notas: string | null;
}

interface Item {
    id?: number;
    descripcion: string;
    cantidad: number;
    unidad: string;
    fob_unit_usd: number;                  // lo que REALMENTE se paga por unidad (la factura)
    fob_declared_unit_usd?: number | null; // valor declarado ante aduana si es MENOR (vacío = igual)
    duty_pct: number;
    orden: number;
    _key?: string;
}

const ALLOWED_EMAILS = ['martin@krilic.com.ar', 'stephanie@krilic.com.ar'];
const IVA_LOCAL = 21; // IVA de los gastos locales cargados con IVA incluido

const fmt = (n: number) => '$' + Math.round(n).toLocaleString('es-AR');
const fmtUSD = (n: number) => 'US$ ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const GASTOS_DEFAULT: Gasto[] = [
    { name: 'Despachante', amount_ars: 0, has_iva: true },
    { name: 'Depósito fiscal', amount_ars: 0, has_iva: true },
    { name: 'Transporte interno', amount_ars: 0, has_iva: true },
    { name: 'Gastos de transferencia', amount_ars: 0, has_iva: false },
    { name: 'Gastos generales / imprevistos', amount_ars: 0, has_iva: false },
];

const emptyImport = (): ImportRow => ({
    fecha: new Date().toISOString().slice(0, 10),
    descripcion: '',
    exchange_rate: 0,
    freight_usd: 0,
    freight_taxable_usd: 0,
    insurance_usd: 0,
    tasa_estadistica_pct: 3,
    iva_pct: 21,
    iva_adic_pct: 20,
    ganancias_pct: 6,
    iibb_pct: 2.5,
    gastos: GASTOS_DEFAULT.map(g => ({ ...g })),
    is_hypothetical: true,
    notas: null,
});

const newItem = (orden: number): Item => ({ descripcion: '', cantidad: 1, unidad: 'u', fob_unit_usd: 0, fob_declared_unit_usd: null, duty_pct: 0, orden, _key: Math.random().toString(36).slice(2) });

export default function Imports({ userEmail }: { userEmail: string | undefined }) {
    const [list, setList] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState<ImportRow | null>(null);
    const [items, setItems] = useState<Item[]>([]);
    const [saving, setSaving] = useState(false);
    // Documentos de IVA compras asignados a esta importación (el despacho real de la Aduana, facturas del forwarder…)
    const [docs, setDocs] = useState<any[]>([]);

    const allowed = !!userEmail && ALLOWED_EMAILS.includes(userEmail);

    const load = useCallback(async () => {
        setLoading(true);
        const { data } = await supabase.from('imports').select('*').order('fecha', { ascending: false });
        setList(data || []);
        setLoading(false);
    }, []);
    useEffect(() => { if (allowed) load(); }, [allowed, load]);

    async function openImport(row: any) {
        setDocs([]);
        if (row.id) supabase.from('iva_documentos').select('*').eq('import_id', row.id).order('fecha').then(({ data }) => setDocs(data || []));
        const { data } = await supabase.from('import_items').select('*').eq('import_id', row.id).order('orden');
        setItems((data || []).map((it: any) => ({ ...it, duty_pct: Number(it.duty_pct) || 0, fob_declared_unit_usd: it.fob_declared_unit_usd ?? null, _key: String(it.id) })));
        setEditing({
            ...row,
            exchange_rate: Number(row.exchange_rate) || 0,
            freight_usd: Number(row.freight_usd) || 0,
            freight_taxable_usd: Number(row.freight_taxable_usd) || 0,
            insurance_usd: Number(row.insurance_usd) || 0,
            tasa_estadistica_pct: Number(row.tasa_estadistica_pct) || 0,
            iva_pct: Number(row.iva_pct) || 0,
            iva_adic_pct: Number(row.iva_adic_pct) || 0,
            ganancias_pct: Number(row.ganancias_pct) || 0,
            iibb_pct: Number(row.iibb_pct) || 0,
            gastos: Array.isArray(row.gastos) && row.gastos.length ? row.gastos : GASTOS_DEFAULT.map(g => ({ ...g })),
        });
    }

    // ───────────────────────── Cálculo del caso completo ─────────────────────────
    const calc = useMemo(() => {
        if (!editing) return null;
        const tc = editing.exchange_rate || 0;
        // fob_unit_usd = lo que REALMENTE se paga. fob_declared_unit_usd = lo que se declara
        // ante aduana si es MENOR (vacío → se declara todo). Los impuestos salen del
        // DECLARADO; el costo real, de lo PAGADO.
        const fobRealUsd = items.reduce((s, it) => s + (Number(it.fob_unit_usd) || 0) * (Number(it.cantidad) || 0), 0);
        const fobUsd = items.reduce((s, it) => {   // declarado (base imponible)
            const dec = Number(it.fob_declared_unit_usd);
            const unit = dec > 0 ? dec : (Number(it.fob_unit_usd) || 0);
            return s + unit * (Number(it.cantidad) || 0);
        }, 0);
        const subdeclarado = fobRealUsd > fobUsd + 0.01;

        // Flete: la parte EXENTA entra al CIF; la GRAVADA son servicios locales del
        // forwarder (no van al CIF) y su IVA es crédito fiscal.
        const freightExento = Number(editing.freight_usd) || 0;
        const freightGravado = Number(editing.freight_taxable_usd) || 0;
        const freightGravadoArs = freightGravado * tc;
        const freightGravadoNeto = freightGravadoArs / (1 + IVA_LOCAL / 100);
        const freightGravadoIva = freightGravadoArs - freightGravadoNeto;

        const cifUsd = fobUsd + freightExento + (editing.insurance_usd || 0);
        const cifArs = cifUsd * tc;
        const fobArs = fobUsd * tc;
        const fobRealArs = fobRealUsd * tc;
        const extraPagado = (fobRealUsd - fobUsd) * tc; // plata que salió pero aduana no vio

        // derechos por producto: cada ítem paga su % sobre su parte del CIF declarado
        const itemsCalc = items.map(it => {
            const itemFobRealUsd = (Number(it.fob_unit_usd) || 0) * (Number(it.cantidad) || 0);
            const decUnit = Number(it.fob_declared_unit_usd) > 0 ? Number(it.fob_declared_unit_usd) : (Number(it.fob_unit_usd) || 0);
            const itemFobUsd = decUnit * (Number(it.cantidad) || 0);   // declarado
            const share = fobUsd > 0 ? itemFobUsd / fobUsd : 0;
            const itemCifArs = cifArs * share;
            const derechosArs = itemCifArs * (Number(it.duty_pct) || 0) / 100;
            return { it, itemFobUsd, itemFobRealUsd, share, itemCifArs, derechosArs };
        });
        const derechosTotal = itemsCalc.reduce((s, x) => s + x.derechosArs, 0);
        const tasa = cifArs * (editing.tasa_estadistica_pct || 0) / 100;
        const baseIva = cifArs + derechosTotal + tasa;

        const iva = baseIva * (editing.iva_pct || 0) / 100;
        const ivaAdic = baseIva * (editing.iva_adic_pct || 0) / 100;
        const gcias = baseIva * (editing.ganancias_pct || 0) / 100;
        const iibb = baseIva * (editing.iibb_pct || 0) / 100;

        const gastos = (editing.gastos || []).map(g => {
            const amount = Number(g.amount_ars) || 0;
            const neto = g.has_iva ? amount / (1 + IVA_LOCAL / 100) : amount;
            return { ...g, amount, neto, ivaGasto: amount - neto };
        });
        const gastosTotal = gastos.reduce((s, g) => s + g.amount, 0);
        const gastosNeto = gastos.reduce((s, g) => s + g.neto, 0);
        const gastosIva = gastosTotal - gastosNeto;

        // Desembolso: todo lo que sale de caja, incluida la diferencia pagada por fuera
        // de la declaración y el flete gravado.
        const desembolso = cifArs + extraPagado + freightGravadoArs + derechosTotal + tasa
            + iva + ivaAdic + gcias + iibb + gastosTotal;
        const deducible = iva + ivaAdic + gcias + iibb + gastosIva + freightGravadoIva;
        const costoReal = desembolso - deducible;
        // Dos coeficientes: sobre el FOB REAL pagado.
        //  · coefFob      = costo real (neto de lo recuperable) / FOB
        //  · coefFobBruto = desembolso total (sin descontar nada) / FOB  ← pedido 21/09
        const baseCoef = fobRealArs > 0 ? fobRealArs : fobArs;
        const coefFob = baseCoef > 0 ? costoReal / baseCoef : 0;
        const coefFobBruto = baseCoef > 0 ? desembolso / baseCoef : 0;

        // ESCENARIO SIN SUBDECLARAR: mismos costos pero declarando el valor real
        const cifSin = (fobRealUsd + freightExento + (editing.insurance_usd || 0)) * tc;
        const derechosSin = itemsCalc.reduce((s, x) => {
            const cifItem = cifSin * (fobRealUsd > 0 ? x.itemFobRealUsd / fobRealUsd : 0);
            return s + cifItem * (Number(x.it.duty_pct) || 0) / 100;
        }, 0);
        const tasaSin = cifSin * (editing.tasa_estadistica_pct || 0) / 100;
        const baseSin = cifSin + derechosSin + tasaSin;
        const pctSum = (editing.iva_pct || 0) + (editing.iva_adic_pct || 0) + (editing.ganancias_pct || 0) + (editing.iibb_pct || 0);
        const percSin = baseSin * pctSum / 100;
        const desembolsoSin = cifSin + freightGravadoArs + derechosSin + tasaSin + percSin + gastosTotal;
        const deducibleSin = percSin + gastosIva + freightGravadoIva;
        const costoRealSin = desembolsoSin - deducibleSin;
        const ahorroCosto = costoRealSin - costoReal;   // lo que se ahorra en costo real
        const ahorroCaja = desembolsoSin - desembolso;  // lo que se ahorra de desembolso

        // costo unitario real por producto: su CIF + su parte real pagada de más + sus
        // derechos + prorrateo (por FOB) de tasa, gastos netos y flete gravado neto
        const prorrateable = tasa + gastosNeto + freightGravadoNeto;
        const itemsFinal = itemsCalc.map(x => {
            const extraItem = (x.itemFobRealUsd - x.itemFobUsd) * tc;
            const costoItem = x.itemCifArs + extraItem + x.derechosArs + prorrateable * x.share;
            const qty = Number(x.it.cantidad) || 0;
            return { ...x, costoItem, unitReal: qty > 0 ? costoItem / qty : 0 };
        });

        return {
            tc, fobUsd, fobArs, fobRealUsd, fobRealArs, subdeclarado, extraPagado,
            freightExento, freightGravado, freightGravadoArs, freightGravadoNeto, freightGravadoIva,
            cifUsd, cifArs, derechosTotal, tasa, baseIva, iva, ivaAdic, gcias, iibb,
            gastos, gastosTotal, gastosNeto, gastosIva,
            desembolso, deducible, costoReal, coefFob, coefFobBruto, itemsFinal,
            costoRealSin, desembolsoSin, ahorroCosto, ahorroCaja,
        };
    }, [editing, items]);

    // ───────────────────────── Persistencia ─────────────────────────
    async function save() {
        if (!editing) return;
        setSaving(true);
        const row: any = {
            fecha: editing.fecha,
            descripcion: editing.descripcion || 'Sin nombre',
            exchange_rate: editing.exchange_rate,
            freight_usd: editing.freight_usd,
            freight_taxable_usd: editing.freight_taxable_usd,
            insurance_usd: editing.insurance_usd,
            tasa_estadistica_pct: editing.tasa_estadistica_pct,
            iva_pct: editing.iva_pct,
            iva_adic_pct: editing.iva_adic_pct,
            ganancias_pct: editing.ganancias_pct,
            iibb_pct: editing.iibb_pct,
            gastos: editing.gastos,
            is_hypothetical: editing.is_hypothetical,
            notas: editing.notas,
        };
        let importId = editing.id;
        if (importId) {
            const { error } = await supabase.from('imports').update(row).eq('id', importId);
            if (error) { alert('Error: ' + error.message); setSaving(false); return; }
        } else {
            const { data, error } = await supabase.from('imports').insert(row).select('id').single();
            if (error || !data) { alert('Error: ' + (error?.message || 'sin id')); setSaving(false); return; }
            importId = data.id;
            setEditing(e => e ? { ...e, id: importId } : e);
        }
        // items: delete + insert (simple y consistente)
        await supabase.from('import_items').delete().eq('import_id', importId);
        const rows = items.filter(it => it.descripcion.trim()).map((it, i) => ({
            import_id: importId, descripcion: it.descripcion, cantidad: it.cantidad,
            unidad: it.unidad || 'u', fob_unit_usd: it.fob_unit_usd,
            fob_declared_unit_usd: (it.fob_declared_unit_usd ?? null) || null, duty_pct: it.duty_pct, orden: i,
        }));
        if (rows.length) {
            const { error } = await supabase.from('import_items').insert(rows);
            if (error) { alert('Error items: ' + error.message); setSaving(false); return; }
        }
        setSaving(false);
        load();
    }

    async function removeImport(row: any) {
        if (!window.confirm(`¿Eliminar "${row.descripcion}"?`)) return;
        await supabase.from('import_items').delete().eq('import_id', row.id);
        await supabase.from('imports').delete().eq('id', row.id);
        load();
    }

    async function duplicateImport(row: any) {
        const { data: its } = await supabase.from('import_items').select('*').eq('import_id', row.id).order('orden');
        const { id, created_at, updated_at, ...rest } = row;
        const { data: nu, error } = await supabase.from('imports').insert({ ...rest, descripcion: row.descripcion + ' (copia)', is_hypothetical: true }).select('id').single();
        if (error || !nu) { alert('Error: ' + error?.message); return; }
        if (its?.length) {
            await supabase.from('import_items').insert(its.map(({ id: _i, created_at: _c, ...r }: any) => ({ ...r, import_id: nu.id })));
        }
        load();
    }

    const upd = (patch: Partial<ImportRow>) => setEditing(e => e ? { ...e, ...patch } : e);
    const updGasto = (i: number, patch: Partial<Gasto>) => upd({ gastos: editing!.gastos.map((g, j) => j === i ? { ...g, ...patch } : g) });
    const updItem = (key: string, patch: Partial<Item>) => setItems(its => its.map(it => (it._key === key ? { ...it, ...patch } : it)));

    if (!allowed) return (
        <div className="tw w-full min-w-0 pb-12 pt-1">
            <div className="rounded-[var(--radius-surface)] border border-line bg-card">
                <EmptyState icon={<Lock size={20} />} title="Acceso restringido" description="Las importaciones solo las ven Martín y Stephanie." />
            </div>
        </div>
    );

    // ───────────────────────── Vista lista ─────────────────────────
    if (!editing) {
        const cols: GridCol<any>[] = [
            { key: 'fecha', header: 'Fecha', sort: r => r.fecha, render: r => <span className="whitespace-nowrap">{new Date(r.fecha + 'T12:00:00').toLocaleDateString('es-AR')}</span> },
            {
                key: 'descripcion', header: 'Descripción', sort: r => r.descripcion || '', render: r => (
                    <span className="inline-flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-ink">{r.descripcion}</span>
                        {r.is_hypothetical && <Badge size="sm" tone="warning" icon={<FlaskConical size={12} />} title="Caso hipotético — no es una importación real todavía">hipotético</Badge>}
                    </span>
                ),
            },
            { key: 'tc', header: 'Tipo de cambio', align: 'right', hideOnMobile: true, sort: r => Number(r.exchange_rate) || 0, render: r => Number(r.exchange_rate) ? '$' + Number(r.exchange_rate).toLocaleString('es-AR') : <span className="text-faint">—</span> },
            {
                key: 'acc', header: '', align: 'right', render: r => (
                    <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                        <Button variant="secondary" size="sm" onClick={() => openImport(r)}>Abrir</Button>
                        <Button variant="ghost" size="sm" title="Duplicar" aria-label="Duplicar" onClick={() => duplicateImport(r)}><Copy size={14} /></Button>
                        <Button variant="ghost" size="sm" title="Eliminar" aria-label="Eliminar" onClick={() => removeImport(r)}><Trash2 size={14} /></Button>
                    </div>
                ),
            },
        ];
        const nueva = () => { setEditing(emptyImport()); setItems([newItem(0)]); };
        return (
            <div className="tw w-full min-w-0 pb-12 pt-1">
                <PageHeader title="Importaciones"
                    description="Costeo completo de cada importación: CIF, derechos por producto, tasa, IVA y percepciones, gastos locales y costo real por unidad. Simulá casos hipotéticos antes de comprar."
                    actions={<Button onClick={nueva}><Plus size={16} /> Nueva importación</Button>} />
                <DataGrid rows={list} cols={cols} loading={loading} onRowClick={openImport}
                    searchPlaceholder="Buscar importación…"
                    emptyTitle="Sin importaciones" emptyText="Creá un caso hipotético para simular el costo antes de comprar."
                    emptyAction={<Button onClick={nueva}><Plus size={16} /> Nueva importación</Button>} />
            </div>
        );
    }

    // ───────────────────────── Vista editor ─────────────────────────
    const c = calc!;
    // Total que se paga en aduana con el VEP: tributos de la liquidación (no incluye CIF, gastos locales ni flete gravado)
    const vep = c.derechosTotal + c.tasa + c.iva + c.ivaAdic + c.gcias + c.iibb;
    const cell = 'h-[var(--control-height-sm)] w-full rounded-[var(--radius-control)] border border-line bg-card px-2 text-[length:var(--text-sm)] tabular-nums text-ink';
    const num = (v: number) => (v || '') as any;
    const row = (label: React.ReactNode, v: number, opts: { usd?: string | null; strong?: boolean; tone?: 'success' | 'danger' } = {}) => (
        <div className={cn('flex items-baseline justify-between gap-3 border-t border-line py-1.5 first:border-0', opts.strong && 'font-semibold text-ink')}
            style={opts.tone ? { color: `var(--${opts.tone})` } : undefined}>
            <span className="min-w-0">{label}{opts.usd ? <span className="ml-2 text-[length:var(--text-xs)] text-faint">{opts.usd}</span> : null}</span>
            <span className="shrink-0 tabular-nums">{fmt(v)}</span>
        </div>
    );

    return (
        <div className="tw w-full min-w-0 pb-12 pt-1">
            <div className="mb-3">
                <Button variant="ghost" size="sm" onClick={() => { setEditing(null); setItems([]); }}><ArrowLeft size={14} /> Importaciones</Button>
            </div>
            <PageHeader
                title={editing.descripcion || 'Nueva importación'}
                description={<>
                    {editing.fecha ? new Date(editing.fecha + 'T12:00:00').toLocaleDateString('es-AR') : 'Sin fecha'}
                    {' · '}TC {c.tc ? '$' + c.tc.toLocaleString('es-AR') : 'sin cargar'}
                    {' · '}FOB pagado {fmtUSD(c.fobRealUsd)}
                </>}
                actions={<>
                    <Checkbox label="Hipotético" checked={editing.is_hypothetical} onCheckedChange={v => upd({ is_hypothetical: v === true })}
                        title="Caso hipotético: para simular; destildalo cuando sea la importación real" />
                    <Button onClick={save} loading={saving}><Save size={16} /> {saving ? 'Guardando…' : 'Guardar'}</Button>
                </>}
            />

            {/* ── Resumen ─────────────────────────────────────────────────────── */}
            <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <StatCard label="Plata total puesta" value={fmt(c.desembolso)}
                    context={<>desembolso completo · <b>{c.coefFobBruto.toFixed(2)}×</b> sobre FOB pagado</>} />
                <StatCard label="Total a pagar en aduana (VEP)" value={fmt(vep)}
                    context="derechos + tasa estadística + IVA + IVA adicional + Ganancias + IIBB" />
                <StatCard label="Deducible / a cuenta" value={fmt(c.deducible)} tone="success"
                    context="IVA, IVA adic., Ganancias, IIBB y crédito de gastos y flete" />
                <StatCard label="Costo real de la mercadería" value={fmt(c.costoReal)}
                    context={<><b>{c.coefFob.toFixed(2)}×</b> sobre FOB pagado</>} />
            </section>

            {/* ── Datos ───────────────────────────────────────────────────────── */}
            <Panel title="Datos" className="mb-5">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                    <Input label="Descripción" value={editing.descripcion} onChange={e => upd({ descripcion: e.target.value })} placeholder="Ej: Contenedor sept 2026" />
                    <Input label="Fecha" type="date" value={editing.fecha} onChange={e => upd({ fecha: e.target.value })} />
                    <Input label="Tipo de cambio (ARS/USD)" type="number" value={num(editing.exchange_rate)} onChange={e => upd({ exchange_rate: Number(e.target.value) || 0 })} placeholder="1520" />
                    <Input label="Flete exento (USD)" type="number" value={num(editing.freight_usd)} onChange={e => upd({ freight_usd: Number(e.target.value) || 0 })}
                        title="Flete marítimo internacional: exento de IVA. Entra al CIF y por lo tanto paga derechos." />
                    <Input label="Flete gravado c/IVA (USD)" type="number" value={num(editing.freight_taxable_usd)} onChange={e => upd({ freight_taxable_usd: Number(e.target.value) || 0 })}
                        title="Parte gravada del flete (servicios locales del forwarder), con IVA incluido. NO entra al CIF y su IVA es crédito fiscal." />
                    <Input label="Seguro (USD)" type="number" value={num(editing.insurance_usd)} onChange={e => upd({ insurance_usd: Number(e.target.value) || 0 })} />
                </div>
            </Panel>

            {/* ── Productos ───────────────────────────────────────────────────── */}
            <Panel className="mb-5" title="Productos"
                right={<Button variant="secondary" size="sm" onClick={() => setItems([...items, newItem(items.length)])}><Plus size={14} /> Producto</Button>}>
                <p className="mb-3 text-[length:var(--text-sm)] text-soft">
                    FOB unitario <b>pagado</b> (la factura) y, si se declara menos, el valor <b>declarado</b> a aduana: los impuestos salen del declarado y el costo real de lo pagado. El derecho se carga por producto.
                </p>
                <div className="overflow-x-auto rounded-[var(--radius-control)] border border-line">
                    <table className="w-full min-w-[760px] border-collapse text-[length:var(--text-sm)]">
                        <thead>
                            <tr className="border-b border-line text-left text-soft">
                                <th className="h-10 px-3 font-medium">Producto</th>
                                <th className="h-10 px-3 text-right font-medium">Cant.</th>
                                <th className="h-10 px-3 text-right font-medium">FOB unit. pagado (USD)</th>
                                <th className="h-10 px-3 text-right font-medium">Declarado a aduana</th>
                                <th className="h-10 px-3 text-right font-medium">Derecho %</th>
                                <th className="h-10 px-3 text-right font-medium">Costo real unit.</th>
                                <th className="h-10 w-10 px-2"><span className="sr-only">Quitar</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {c.itemsFinal.map(({ it, itemFobUsd, itemFobRealUsd, unitReal }) => (
                                <tr key={it._key} className="border-b border-line align-top last:border-0">
                                    <td className="px-3 py-2"><input className={cell} value={it.descripcion} onChange={e => updItem(it._key!, { descripcion: e.target.value })} placeholder="Ej: Libro XL Ferrari" aria-label="Producto" /></td>
                                    <td className="px-3 py-2"><input type="number" className={cn(cell, 'w-20 text-right')} value={num(it.cantidad)} onChange={e => updItem(it._key!, { cantidad: Number(e.target.value) || 0 })} aria-label="Cantidad" /></td>
                                    <td className="px-3 py-2 text-right">
                                        <input type="number" className={cn(cell, 'w-28 text-right')} value={num(it.fob_unit_usd)} onChange={e => updItem(it._key!, { fob_unit_usd: Number(e.target.value) || 0 })}
                                            title="Lo que REALMENTE pagás por unidad (la factura del proveedor)" aria-label="FOB unitario pagado" />
                                        <div className="mt-1 text-[length:var(--text-xs)] text-faint">{fmtUSD(itemFobRealUsd)}</div>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <input type="number" className={cn(cell, 'w-28 text-right')} value={it.fob_declared_unit_usd ?? ''} placeholder="= pagado" aria-label="Valor declarado"
                                            onChange={e => updItem(it._key!, { fob_declared_unit_usd: e.target.value === '' ? null : Number(e.target.value) })}
                                            title="Valor unitario que se declara ante aduana, si es MENOR al pagado. Vacío = se declara todo. Los impuestos se calculan sobre este número." />
                                        {itemFobRealUsd > itemFobUsd + 0.01 && (
                                            <div className="mt-1 text-[length:var(--text-xs)] font-semibold text-[var(--warning)]">−{fmtUSD(itemFobRealUsd - itemFobUsd)} sin declarar</div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right"><input type="number" className={cn(cell, 'w-20 text-right')} value={num(it.duty_pct)} onChange={e => updItem(it._key!, { duty_pct: Number(e.target.value) || 0 })} placeholder="0" aria-label="Derecho %" /></td>
                                    <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-ink leading-[var(--control-height-sm)]">{unitReal > 0 ? fmt(unitReal) : '—'}</td>
                                    <td className="px-2 py-2 text-right">
                                        <Button variant="ghost" size="sm" aria-label="Quitar producto" title="Quitar producto" onClick={() => setItems(its => its.filter(x => x._key !== it._key))}><Trash2 size={14} /></Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>

            <div className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
                {/* Parámetros impositivos */}
                <Panel title="Impuestos y percepciones">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Input label="Tasa estadística % (s/CIF)" type="number" value={editing.tasa_estadistica_pct} onChange={e => upd({ tasa_estadistica_pct: Number(e.target.value) || 0 })} />
                        <Input label="IVA % (s/base)" type="number" value={editing.iva_pct} onChange={e => upd({ iva_pct: Number(e.target.value) || 0 })} />
                        <Input label="IVA adicional % (0 si no aplica)" type="number" value={editing.iva_adic_pct} onChange={e => upd({ iva_adic_pct: Number(e.target.value) || 0 })} />
                        <Input label="Adelanto Ganancias %" type="number" value={editing.ganancias_pct} onChange={e => upd({ ganancias_pct: Number(e.target.value) || 0 })} />
                        <Input label="Percepción IIBB %" type="number" value={editing.iibb_pct} onChange={e => upd({ iibb_pct: Number(e.target.value) || 0 })} />
                    </div>
                    <p className="mt-3 text-[length:var(--text-xs)] leading-relaxed text-faint">
                        Base de IVA y percepciones = CIF + derechos + tasa estadística.
                    </p>
                </Panel>

                {/* Gastos locales */}
                <Panel title="Gastos locales (con IVA incluido)"
                    right={<Button variant="secondary" size="sm" onClick={() => upd({ gastos: [...editing.gastos, { name: '', amount_ars: 0, has_iva: false }] })}><Plus size={14} /> Gasto</Button>}>
                    <div className="flex flex-col gap-2">
                        {editing.gastos.map((g, i) => (
                            <div key={i} className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                                <input className={cn(cell, 'min-w-0 flex-1')} value={g.name} onChange={e => updGasto(i, { name: e.target.value })} placeholder="Nombre del gasto" aria-label="Nombre del gasto" />
                                <input type="number" className={cn(cell, 'w-32 text-right')} value={num(g.amount_ars)} onChange={e => updGasto(i, { amount_ars: Number(e.target.value) || 0 })} placeholder="$" aria-label="Monto" />
                                <span title="Si tiene IVA, el 21% incluido es crédito fiscal (deducible)">
                                    <Checkbox label="c/IVA" checked={g.has_iva} onCheckedChange={v => updGasto(i, { has_iva: v === true })} />
                                </span>
                                <Button variant="ghost" size="sm" aria-label="Quitar gasto" onClick={() => upd({ gastos: editing.gastos.filter((_, j) => j !== i) })}><X size={14} /></Button>
                            </div>
                        ))}
                    </div>
                </Panel>
            </div>

            {/* ── Resultado ───────────────────────────────────────────────────── */}
            <Panel className="mb-5" title="Resultado del caso" right={<span className="text-[length:var(--text-sm)] text-soft">TC ${(c.tc || 0).toLocaleString('es-AR')}</span>}>
                <div className="grid grid-cols-1 gap-5 text-[length:var(--text-sm)] text-soft lg:grid-cols-2">
                    <div>
                        <div className="mb-1 text-[length:var(--text-xs)] font-semibold uppercase tracking-wide text-faint">Lo que ve aduana</div>
                        {row('FOB declarado a aduana', c.fobArs, { usd: fmtUSD(c.fobUsd) })}
                        {row('+ Flete exento + seguro', c.cifArs - c.fobArs, { usd: fmtUSD(c.cifUsd - c.fobUsd) })}
                        {row('= CIF (lo que ve aduana)', c.cifArs, { usd: fmtUSD(c.cifUsd), strong: true })}
                        {row('+ Derechos de importación (por producto)', c.derechosTotal)}
                        {row(`+ Tasa estadística (${editing.tasa_estadistica_pct}%)`, c.tasa)}
                        {row('= Base IVA / percepciones', c.baseIva, { strong: true })}
                        <div className="mt-2">
                            {row(<>Gastos locales (con IVA): {c.gastos.filter(g => g.amount > 0).map(g => g.name).join(', ') || '—'}</>, c.gastosTotal)}
                        </div>
                    </div>
                    <div className="flex flex-col gap-3">
                        {/* Liquidación de aduana: lo que se paga con el VEP. Solo suma líneas ya calculadas. */}
                        <div className="rounded-[var(--radius-control)] border border-line p-3">
                            <div className="mb-1 text-[length:var(--text-xs)] font-semibold uppercase tracking-wide text-faint">Se paga en aduana (VEP)</div>
                            {row('Derechos de importación', c.derechosTotal)}
                            {row(`Tasa estadística (${editing.tasa_estadistica_pct}%)`, c.tasa)}
                            {row(`IVA importación (${editing.iva_pct}%)`, c.iva)}
                            {row(`IVA adicional (${editing.iva_adic_pct}%)`, c.ivaAdic)}
                            {row(`Adelanto Ganancias (${editing.ganancias_pct}%)`, c.gcias)}
                            {row(`Percepción IIBB (${editing.iibb_pct}%)`, c.iibb)}
                            {row('Total a pagar en aduana (VEP)', vep, { strong: true })}
                        </div>
                        <div className="rounded-[var(--radius-control)] border p-3"
                            style={{ borderColor: 'color-mix(in oklab, var(--success) 30%, var(--border))', background: 'color-mix(in oklab, var(--success) 6%, var(--card))', color: 'var(--success)' }}>
                            <div className="mb-1 text-[length:var(--text-xs)] font-semibold uppercase tracking-wide">Deducible / a cuenta (se recupera)</div>
                            {row(`IVA importación (${editing.iva_pct}%)`, c.iva)}
                            {row(`IVA adicional (${editing.iva_adic_pct}%)`, c.ivaAdic)}
                            {row(`Adelanto Ganancias (${editing.ganancias_pct}%)`, c.gcias)}
                            {row(`Percepción IIBB (${editing.iibb_pct}%)`, c.iibb)}
                            {row('IVA dentro de gastos locales', c.gastosIva)}
                            {row('IVA del flete gravado', c.freightGravadoIva)}
                            {row('Total DEDUCIBLE / a cuenta', c.deducible, { strong: true, tone: 'success' })}
                        </div>
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 border-t border-line pt-4 sm:grid-cols-3">
                    <div><div className="text-[length:var(--text-sm)] text-soft">Plata total puesta</div><div className="text-[length:var(--text-xl)] font-semibold tabular-nums text-ink">{fmt(c.desembolso)}</div><div className="text-[length:var(--text-xs)] text-faint">coeficiente {c.coefFobBruto.toFixed(2)}× sobre FOB pagado (sin deducciones)</div></div>
                    <div><div className="text-[length:var(--text-sm)] text-soft">− Deducible / a cuenta</div><div className="text-[length:var(--text-xl)] font-semibold tabular-nums text-[var(--success)]">{fmt(c.deducible)}</div></div>
                    <div><div className="text-[length:var(--text-sm)] text-soft">= Costo real de la mercadería</div><div className="text-[length:var(--text-xl)] font-semibold tabular-nums text-ink">{fmt(c.costoReal)}</div><div className="text-[length:var(--text-xs)] text-faint">coeficiente {c.coefFob.toFixed(2)}× sobre FOB pagado</div></div>
                </div>
            </Panel>

            {/* ── Despacho real (documentos de IVA compras asignados a esta importación) ── */}
            {docs.length > 0 && (() => {
                const desp = docs.filter(d => d.kind === 'despacho');
                const sum = (k: string) => desp.reduce((a, d) => a + (Number(d[k]) || 0), 0);
                const lines: [string, number, number][] = [
                    ['Derechos + tasa estadística + arancel', c.derechosTotal + c.tasa, sum('otros_tributos')],
                    ['IVA importación', c.iva, sum('iva_total')],
                    ['IVA adicional', c.ivaAdic, sum('iva_adicional')],
                    ['Adelanto Ganancias', c.gcias, sum('percepcion_ganancias')],
                    ['Percepción IIBB', c.iibb, sum('percepcion_iibb')],
                    ['Total pagado en aduana (VEP)', vep, sum('total')],
                ];
                return (
                    <Panel className="mb-5" title="Despacho real" right={<a className="text-[length:var(--text-sm)] text-soft underline" href="/costos?tab=iva">Ver en IVA compras</a>}>
                        <ul className="mb-3 flex flex-col gap-1 text-[length:var(--text-sm)] text-soft">
                            {docs.map(d => (
                                <li key={d.id}>
                                    <b className="text-ink">{d.kind === 'despacho' ? 'Despacho' : 'Comprobante'} {d.comprobante || d.file_name}</b>
                                    {d.fecha ? ` · ${new Date(d.fecha + 'T12:00:00').toLocaleDateString('es-AR')}` : ''}
                                    {d.tipo_cambio ? ` · TC $${Number(d.tipo_cambio).toLocaleString('es-AR')}` : ''}
                                    {d.notas ? <div className="text-[length:var(--text-xs)] text-faint">{d.notas}</div> : null}
                                </li>
                            ))}
                        </ul>
                        {desp.length > 0 && (
                            <div className="overflow-x-auto rounded-[var(--radius-control)] border border-line">
                                <table className="w-full min-w-[520px] border-collapse text-[length:var(--text-sm)]">
                                    <thead>
                                        <tr className="border-b border-line text-soft">
                                            <th className="h-10 px-3 text-left font-medium">Concepto</th>
                                            <th className="h-10 px-3 text-right font-medium">Simulado (TC ${(c.tc || 0).toLocaleString('es-AR')})</th>
                                            <th className="h-10 px-3 text-right font-medium">Real (despacho)</th>
                                            <th className="h-10 px-3 text-right font-medium">Diferencia</th>
                                        </tr>
                                    </thead>
                                    <tbody className="tabular-nums">
                                        {lines.map(([label, sim, real], i) => (
                                            <tr key={label} className={cn('border-b border-line last:border-0', i === lines.length - 1 && 'font-semibold text-ink')}>
                                                <td className="px-3 py-2">{label}</td>
                                                <td className="px-3 py-2 text-right text-soft">{fmt(sim)}</td>
                                                <td className="px-3 py-2 text-right text-ink">{fmt(real)}</td>
                                                <td className="px-3 py-2 text-right" style={{ color: `var(--${real > sim ? 'danger' : 'success'})` }}>{real - sim > 0 ? '+' : real - sim < 0 ? '−' : ''}{fmt(Math.abs(real - sim))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Panel>
                );
            })()}

            {/* ── Escenario: declarar menos vs declarar el valor real ─────────── */}
            {c.subdeclarado && (
                <Panel className="mb-5" title={<span className="text-[var(--warning)]">Escenario: declarado vs. real pagado</span>}>
                    <p className="mb-3 text-[length:var(--text-sm)] text-soft">
                        Pagás <b>{fmtUSD(c.fobRealUsd)}</b> y declarás <b>{fmtUSD(c.fobUsd)}</b>:{' '}
                        <b>{fmtUSD(c.fobRealUsd - c.fobUsd)}</b> ({fmt(c.extraPagado)}) salen de caja sin pasar por aduana.
                        Los impuestos se calculan sobre lo declarado; el costo real, sobre todo lo que pagás.
                    </p>
                    <div className="overflow-x-auto rounded-[var(--radius-control)] border border-line">
                        <table className="w-full min-w-[560px] border-collapse text-[length:var(--text-sm)]">
                            <thead>
                                <tr className="border-b border-line text-soft">
                                    <th className="h-10 px-3 text-left font-medium"><span className="sr-only">Concepto</span></th>
                                    <th className="h-10 px-3 text-right font-medium">Declarando {fmtUSD(c.fobUsd)}</th>
                                    <th className="h-10 px-3 text-right font-medium">Declarando todo {fmtUSD(c.fobRealUsd)}</th>
                                    <th className="h-10 px-3 text-right font-medium">Ahorro</th>
                                </tr>
                            </thead>
                            <tbody className="tabular-nums">
                                <tr className="border-b border-line">
                                    <td className="px-3 py-2">Plata total puesta</td>
                                    <td className="px-3 py-2 text-right font-semibold text-ink">{fmt(c.desembolso)}</td>
                                    <td className="px-3 py-2 text-right text-soft">{fmt(c.desembolsoSin)}</td>
                                    <td className="px-3 py-2 text-right font-semibold text-[var(--success)]">−{fmt(c.ahorroCaja)}</td>
                                </tr>
                                <tr className="border-b border-line">
                                    <td className="px-3 py-2 font-semibold text-ink">Costo real de la mercadería</td>
                                    <td className="px-3 py-2 text-right font-semibold text-ink">{fmt(c.costoReal)}</td>
                                    <td className="px-3 py-2 text-right text-soft">{fmt(c.costoRealSin)}</td>
                                    <td className="px-3 py-2 text-right font-semibold text-[var(--success)]">−{fmt(c.ahorroCosto)}</td>
                                </tr>
                                <tr>
                                    <td className="px-3 py-2">Coeficiente sobre FOB pagado</td>
                                    <td className="px-3 py-2 text-right">{c.coefFob.toFixed(2)}×</td>
                                    <td className="px-3 py-2 text-right text-soft">{(c.fobRealArs > 0 ? c.costoRealSin / c.fobRealArs : 0).toFixed(2)}×</td>
                                    <td className="px-3 py-2 text-right text-faint">—</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="mt-2 text-[length:var(--text-xs)] leading-relaxed text-faint">
                        El ahorro sale de que derechos, tasa, IVA y percepciones se calculan sobre el CIF declarado.
                        Ojo: parte de lo que se "ahorra" es IVA y percepciones, que igual se recuperan — por eso la
                        fila que importa es la del <b>costo real</b>, no la del desembolso.
                    </p>
                </Panel>
            )}

            <Panel>
                <Textarea label="Notas" value={editing.notas || ''} onChange={e => upd({ notas: e.target.value })} placeholder="Observaciones, proveedor, contenedor…" />
            </Panel>
        </div>
    );
}
